import React from 'react';
import { ArrowLeftIcon, MapPinIcon, StarIcon, ClockIcon, PhoneIcon, GlobeIcon, FlameIcon } from 'lucide-react';
import { restaurants, cantones } from '../data/mockData';
import { Card } from './Card';
import { CTAButton } from './CTAButton';
interface RestaurantDetailProps {
  restaurantId: string;
  onBack: () => void;
}
export function RestaurantDetail({
  restaurantId,
  onBack
}: RestaurantDetailProps) {
  const restaurant = restaurants.find(r => r.id === restaurantId);
  if (!restaurant) return null;
  const canton = cantones.find(c => c.id === restaurant.cantonId);
  return <div className="min-h-screen bg-bg-primary pb-24" data-id="element-277">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px] w-full" data-id="element-278">
        <img src={restaurant.image} alt={restaurant.name} className="w-full h-full object-cover" data-id="element-279" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-black/50 to-transparent" data-id="element-280" />

        <div className="absolute top-6 left-4 md:left-8 z-10" data-id="element-281">
          <button onClick={onBack} className="flex items-center gap-2 bg-bg-primary/60 backdrop-blur-md text-white px-4 py-2 rounded-full font-body text-sm font-medium hover:bg-bg-primary/80 transition-colors border border-border" data-id="element-282">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-283" /> Volver
          </button>
        </div>

        <div className="absolute bottom-0 left-0 w-full p-6 md:p-12" data-id="element-284">
          <div className="max-w-7xl mx-auto" data-id="element-285">
            <div className="flex items-center gap-3 mb-3" data-id="element-286">
              <span className="bg-brand-gold text-bg-primary px-3 py-1 rounded-full font-body text-xs font-bold uppercase tracking-wider" data-id="element-287">
                {restaurant.categoryId}
              </span>
              <span className="flex items-center gap-1 text-white font-body font-medium" data-id="element-288">
                <StarIcon className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-289" />
                {restaurant.rating} ({restaurant.reviewCount} reseñas)
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-heading uppercase tracking-wide text-white mb-2" data-id="element-290">
              {restaurant.name}
            </h1>
            <p className="font-body text-white/80 text-lg max-w-2xl" data-id="element-291">
              {restaurant.description}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-292">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-293">
          <div className="lg:col-span-2 space-y-12" data-id="element-294">
            {/* Menu */}
            <section data-id="element-295">
              <div className="flex items-center gap-2 mb-6" data-id="element-296">
                <FlameIcon className="w-6 h-6 text-brand-orange" data-id="element-297" />
                <h2 className="text-3xl font-heading uppercase tracking-wide text-white" data-id="element-298">
                  Menú Destacado
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-299">
                {restaurant.menuItems.map((item, idx) => <Card key={idx} className="p-5" data-id="element-300">
                    <div className="flex justify-between items-start mb-2" data-id="element-301">
                      <h4 className="font-body font-bold text-white" data-id="element-302">
                        {item.name}
                      </h4>
                      <span className="font-body font-semibold text-brand-gold whitespace-nowrap ml-4" data-id="element-303">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-body text-sm text-text-secondary leading-relaxed" data-id="element-304">
                      {item.description}
                    </p>
                  </Card>)}
              </div>
            </section>

            {/* Reviews */}
            <section data-id="element-305">
              <h2 className="text-3xl font-heading uppercase tracking-wide text-white mb-6" data-id="element-306">
                Reseñas
              </h2>
              <div className="space-y-4" data-id="element-307">
                {restaurant.reviews.map((review, idx) => <Card key={idx} className="p-6" data-id="element-308">
                    <div className="flex justify-between items-start mb-4" data-id="element-309">
                      <div className="flex items-center gap-3" data-id="element-310">
                        <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center font-heading text-xl text-brand-gold" data-id="element-311">
                          {review.author.charAt(0)}
                        </div>
                        <div data-id="element-312">
                          <p className="font-body font-bold text-white" data-id="element-313">
                            {review.author}
                          </p>
                          <p className="font-body text-xs text-text-muted" data-id="element-314">
                            {review.date}
                          </p>
                        </div>
                      </div>
                      <div className="flex" data-id="element-315">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'text-brand-gold fill-brand-gold' : 'text-border'}`} data-id="element-316" />)}
                      </div>
                    </div>
                    <p className="font-body text-text-secondary italic" data-id="element-317">
                      "{review.text}"
                    </p>
                  </Card>)}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-318">
            <Card className="p-6" data-id="element-319">
              <h3 className="font-heading text-2xl text-white mb-4" data-id="element-320">
                Información
              </h3>
              <div className="space-y-4 font-body text-sm" data-id="element-321">
                <div className="flex items-start gap-3 text-text-secondary" data-id="element-322">
                  <MapPinIcon className="w-5 h-5 text-brand-orange flex-shrink-0" data-id="element-323" />
                  <div data-id="element-324">
                    <p className="font-semibold text-white" data-id="element-325">
                      {restaurant.address}
                    </p>
                    <p data-id="element-326">{canton?.fullName}, Zona de Los Santos</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-text-secondary" data-id="element-327">
                  <ClockIcon className="w-5 h-5 text-brand-orange flex-shrink-0" data-id="element-328" />
                  <p data-id="element-329">Abierto hoy: 11:30 AM - 10:00 PM</p>
                </div>
                <div className="flex items-center gap-3 text-text-secondary" data-id="element-330">
                  <span className="w-5 h-5 flex items-center justify-center font-bold text-brand-gold flex-shrink-0" data-id="element-331">
                    $
                  </span>
                  <p data-id="element-332">
                    Rango de precio:{' '}
                    <span className="font-semibold text-white" data-id="element-333">
                      {restaurant.priceRange}
                    </span>
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-border" data-id="element-334">
                <p className="font-body text-sm font-semibold text-white mb-3" data-id="element-335">
                  Etiquetas
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-336">
                  {restaurant.tags.map((tag, idx) => <span key={idx} className="bg-bg-secondary text-text-secondary text-xs font-body px-2.5 py-1.5 rounded-md border border-border" data-id="element-337">
                      {tag}
                    </span>)}
                </div>
              </div>
            </Card>

            <Card className="p-6" data-id="element-338">
              <div className="space-y-3" data-id="element-339">
                <CTAButton className="w-full flex items-center justify-center gap-2" data-id="element-340">
                  <MapPinIcon className="w-4 h-4" data-id="element-341" /> Ver en Mapa
                </CTAButton>
                <button className="w-full py-3 rounded-lg border border-border font-body font-bold text-white hover:bg-bg-secondary transition-colors flex items-center justify-center gap-2" data-id="element-342">
                  <PhoneIcon className="w-4 h-4" data-id="element-343" /> Llamar
                </button>
                <button className="w-full py-3 rounded-lg border border-border font-body font-bold text-white hover:bg-bg-secondary transition-colors flex items-center justify-center gap-2" data-id="element-344">
                  <GlobeIcon className="w-4 h-4" data-id="element-345" /> Sitio Web
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}